import {
  AccountInfo,
  InteractionRequiredAuthError,
  PopupRequest,
  PublicClientApplication,
  SilentRequest,
} from '@azure/msal-browser';

import {
  AcquireTokenAuthError,
  AcquireTokenPopupError,
  InteractiveLoginRequiredAuthError,
  LoginInProgressAuthError,
  LoginPopupError,
  SsoSilentError,
} from '../errors';
import { getMsalErrorDetails } from '../getMsalErrorDetails';
import { AuthenticatorOptions, AuthenticatorType, PopupAuthenticator } from '../types';

import { loggerOptions } from './loggerOptions';

export default class MsalPopupAuthenticator implements PopupAuthenticator {
  public readonly type = AuthenticatorType.MsalPopup;
  private readonly application: PublicClientApplication;
  private currentAccount: AccountInfo | null;
  private readonly onError: AuthenticatorOptions['onError'];
  private instance: string;

  constructor({ clientId, instance, onError, postLogoutRedirectUri, redirectUri, tenant }: AuthenticatorOptions) {
    this.instance = instance;
    this.application = new PublicClientApplication({
      auth: {
        authority: `${instance}${tenant}`,
        clientId,
        navigateToLoginRequestUrl: true,
        postLogoutRedirectUri,
        redirectUri,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions,
        pollIntervalMilliseconds: 15,
      },
    });

    this.currentAccount = null;
    this.onError = onError;
  }

  public async acquireToken(scopes: string[], claims?: string, tenantId?: string) {
    const currentAccount = this.getCurrentAccount() ?? (await this.ssoSilent(scopes, claims));

    if (!currentAccount) {
      throw new LoginInProgressAuthError('Not yet authenticated. Redirecting to interactive login flow.');
    }

    const account = tenantId
      ? {
          ...currentAccount,
          tenantId,
        }
      : currentAccount;

    const authority = tenantId ? { authority: `${this.instance}${tenantId}` } : {};
    const silentRequestClaims = claims ? { claims } : {};

    const silentRequest: SilentRequest = {
      account,
      scopes,
      ...authority,
      ...silentRequestClaims,
    };

    try {
      const { accessToken } = await this.application.acquireTokenSilent(silentRequest);

      return accessToken;
    } catch (error) {
      const errorDetails = getMsalErrorDetails(error);

      await this.reportError(new AcquireTokenAuthError({ ...errorDetails, attempts: 1 }), { scopes });

      if (error instanceof InteractionRequiredAuthError) {
        throw new InteractiveLoginRequiredAuthError(error.message);
      }

      throw error;
    }
  }

  public acquireTokenRedirect() {
    throw new Error('acquireTokenRedirect is not implemented');
  }

  public getCurrentAccount() {
    return this.currentAccount;
  }

  public async initializeCurrentAccount() {
    await this.application.initialize();
    this.currentAccount = this.getCachedAccount();
  }

  public logout() {
    return this.application.logoutPopup();
  }

  public setThirdPartyCookieErrorCallback() {
    throw new Error('setThirdPartyCookieErrorCallback is not implemented');
  }

  public async acquireTokenPopup(scopes: string[]): Promise<void> {
    try {
      const acquireTokenRequest: PopupRequest = {
        scopes,
      };

      await this.application.acquireTokenPopup(acquireTokenRequest);
      location.reload();
    } catch (error) {
      this.reportError(new AcquireTokenPopupError(error.message));
    }
  }

  private getCachedAccount() {
    const accounts = this.application.getAllAccounts();
    if (accounts.length < 1) {
      return null;
    }

    return accounts[0];
  }

  public async loginPopup(scopes: string[]) {
    try {
      const loginPopupRequest: PopupRequest = {
        scopes,
      };

      await this.application.loginPopup(loginPopupRequest);
      location.reload();
    } catch (error) {
      this.reportError(new LoginPopupError(error.message));
    }
  }

  private async ssoSilent(scopes: string[], claims?: string) {
    try {
      const response = await this.application.ssoSilent({ scopes, claims });
      this.currentAccount = response.account;

      return response.account;
    } catch (error) {
      const errorDetails = getMsalErrorDetails(error);
      await this.reportError(new SsoSilentError(errorDetails));
    }
  }

  private async reportError(error: unknown, properties: { readonly scopes?: string[] } = {}) {
    // istanbul ignore else
    if (this.onError) {
      try {
        await this.onError(error, { authenticatorType: this.type, ...properties });
      } catch (err) {
        console.error(err);
      }
    }
  }
}
